import gql from 'graphql-tag';
import { PayoutSettingsFragment } from './fragments';

const AppListingFragment = gql`
    fragment AppListingFragment on AppListing {
        id
        creationDate
        language
        name
        tagLine
        description
        searchTerms
        integrations
        promoVideoUrl
        includeKeyBenefitsImages
        supportEmail
        supportPhone
        websiteUrl
        privacyPolicyUrl
        termsOfServiceUrl
        offerDemoRequest
        showContactDetails
        icon {
            s3Key
            fileName
            fileType
            height
            width
            size
            sourceUrl
        }
        screenShots {
            type
            description
            image {
                s3Key
                fileName
                fileType
                height
                width
                size
                sourceUrl
            }
        }
        keyBenefits {
            title
            description
            image {
                s3Key
                fileName
                fileType
                height
                width
                size
                sourceUrl
            }
        }
        pricePlans {
            planId
            name
            features
            plan {
                id
                appId
                creationDate
                name
                type
                monthlyAmount
                yearlyAmount
                discountedYearlyPrice
            }
        }
    }
`;

const AppFragment = gql`
    ${AppListingFragment}
    fragment AppFragment on App {
        id
        name
        creationDate
        status
        type
        apiContactEmail
        apiCredentials {
            apiKey
            secretKey
        }
        numberOfInstallations
        linkedVendor {
            id
            name
        }
        appUrl
        redirectUrlWhitelist
        chargeType
        installationCountries
        customInstallation {
            id
        }
        testInstallation {
            id
            creationDate
        }
        oneTimeChargeAmount
        freeTrialDays
        appReviewInstructions
        pricePlans {
            id
            creationDate
            name
            type
            discountedYearlyPrice
            monthlyAmount
            yearlyAmount
            appId
        }
        webhookSubscriptions {
            id
            creationDate
            topic
            url
        }
        listings {
            id
            ...AppListingFragment
        }
    }
`;

/* =================================================
        ORGANIZATIONS
   ================================================= */

export const DASHBOARD_QUERY = gql`
    query GetAppDashboardQuery(
        $timestampFrom: Long!
        $timestampTo: Long!
        $split: AppDashboardSplitType
    ) {
        AppDashboardQuery(timestampFrom: $timestampFrom, timestampTo: $timestampTo, split: $split) {
            installations {
                label
                amount
            }
            uninstallations {
                label
                amount
            }
            installationsByCountry {
                amount
                label
            }
        }
    }
`;

export const ORG_LIST_QUERY = gql`
    query AppOrgListQuery {
        GetOrganizations {
            id
            businessName
            supportEmail
            organizationId
        }
    }
`;

export const ORG_QUERY = gql`
    query AppOrgQuery($id: ID!) {
        appOrg(id: $id) {
            id
            businessName
            businessEmail
            supportEmail
            phoneNumber
            websiteUrl
            emergencyDeveloperEmail
            emergencyDeveloperPhoneNumber
            termsApiAgreed
            termsPartnerAgreed
        }
    }
`;

export const ORG_STATUS_QUERY = gql`
    query AppOrgStatusQuery {
        appOrgStatus {
            hasRejectedPayout
            hasUnclaimedPayout
            periodFrom
            periodTo
            hasValidPayoutSettings
            hasValidTaxForm
            payoutSettingsPreview
            amount
            displayAmount
        }
    }
`;

export const ORG_PAYMENT_SETTINGS = gql`
    query GetPayoutSettings {
        getPayoutSettings {
            ...PayoutSettingsCommon
        }
    }

    ${PayoutSettingsFragment}
`;

export const ORG_DEVELOPER_PAYOUTS = gql`
    query GetDeveloperPayouts($after: String, $first: Int!) {
        getPayouts(first: $first, after: $after) {
            edges {
                node {
                    id
                    amount
                    creationDate
                    invoiceDateFrom
                    invoiceDateTo
                    pdfInvoiceUrl
                    status
                }
                cursor
            }
            pageInfo {
                endCursor
                hasNextPage
                hasPreviousPage
                startCursor
            }
            totalCount
        }
    }
`;

export const ORG_DEVELOPER_PAYOUT = gql`
    query GetDeveloperPayout($id: ID!) {
        getPayoutDetails(id: $id) {
            status
            reason
            payoutInvoice {
                fileName
                fileType
                s3Key
                id
                size
            }
            pdfInvoiceUrl
        }
    }
`;

export const GET_CURRENCIES = gql`
    query Currencies {
        GetCurrencies {
            label
            value
        }
    }
`;

/* =================================================
        APPS
   ================================================= */

export const APP_LIST_QUERY = gql`
    query AppListQuery($after: String, $first: Int!) {
        apps(first: $first, after: $after) {
            totalCount
            pageInfo {
                endCursor
                startCursor
                hasPreviousPage
                hasNextPage
            }
            edges {
                cursor
                node {
                    id
                    name
                    creationDate
                    status
                    type
                    numberOfInstallations
                    linkedVendor {
                        id
                        name
                    }
                }
            }
        }
    }
`;

export const APP_QUERY = gql`
    ${AppFragment}
    query AppQuery($id: ID!) {
        app(id: $id) {
            id
            ...AppFragment
        }
    }
`;

export const APP_VALIDATION_REPORT_QUERY = gql`
    query AppValidationReportQuery($id: ID!) {
        appValidationReport(id: $id) {
            valid
            testInstallationSuccessful
            items {
                valid
                type
                message
            }
            listingsReports {
                listingId
                language
                valid
                items {
                    valid
                    type
                    message
                }
            }
        }
    }
`;

/* =================================================
        USERS
   ================================================= */
export const USER_QUERY = gql`
    query AppUserAccountQuery($id: ID!) {
        appAccountUser(id: $id) {
            id
            email
            firstName
            lastName
            orgId
            orgName
            organizationId
            adminUser
        }
    }
`;

export const USER_LIST_QUERY = gql`
    query UserListQuery(
        $filter: AppUserAccountFilterInput
        $sorting: AppUserAccountSortInput
        $after: String
        $first: Int!
    ) {
        appAccountUserList(filter: $filter, sorting: $sorting, first: $first, after: $after) {
            totalCount
            edges {
                cursor
                node {
                    id
                    email
                    firstName
                    lastName
                    accountOwner
                }
            }
        }
    }
`;

/* =================================================
        APP LISTINGS
   ================================================= */

export const PUBLIC_APP_LISTINGS_QUERY = gql`
    query PublicAppListingsQuery(
        $filter: AppListingFilterInput
        $sorting: AppListingSortInput
        $after: String
        $first: Int!
    ) {
        publicAppListings(filter: $filter, sorting: $sorting, first: $first, after: $after) {
            totalCount
            edges {
                cursor
                node {
                    id
                    appId
                    name
                    tagLine
                    chargeType
                    freeTrialDays
                    oneTimeChargeAmount
                    pricePlans {
                        planId
                        name
                        plan {
                            oneTimeAmount
                            monthlyAmount
                            yearlyAmount
                            bookingFeePercentage
                        }
                    }
                    icon {
                        sourceUrl
                    }
                }
            }
        }
    }
`;

export const PUBLIC_APP_LISTING_BY_ID_QUERY = gql`
    query PublicAppListingByIdQuery($id: ID!) {
        publicAppListing(id: $id) {
            id
            appId
            name
            tagLine
            organization {
                id
                businessName
                businessEmail
                websiteUrl
                phoneNumber
                supportEmail
            }
            description
            promoVideoUrl
            supportEmail
            supportPhone
            websiteUrl
            privacyPolicyUrl
            termsOfServiceUrl
            includeKeyBenefitsImages
            icon {
                s3Key
                fileName
                fileType
                height
                width
                size
                sourceUrl
                temp
            }
            screenShots {
                type
                description
                image {
                    s3Key
                    fileName
                    fileType
                    height
                    width
                    size
                    sourceUrl
                }
            }
            keyBenefits {
                title
                description
                image {
                    s3Key
                    fileName
                    fileType
                    height
                    width
                    size
                    sourceUrl
                }
            }
            appUrl
            chargeType
            freeTrialDays
            oneTimeChargeAmount
            pricePlans {
                planId
                name
                features
                plan {
                    id
                    type
                    monthlyAmount
                    yearlyAmount
                    discountedYearlyPrice
                    bookingFeePercentage
                }
            }
        }
    }

    ${AppListingFragment}
`;

/* =================================================
        INSTALLATIONS
   ================================================= */

export const APP_INSTALLATION_LIST_QUERY = gql`
    query AppInstallationsQuery($appId: ID!, $after: String, $first: Int!) {
        installations(id: $appId, first: $first, after: $after) {
            totalCount
            pageInfo {
                endCursor
                startCursor
                hasPreviousPage
                hasNextPage
            }
            edges {
                cursor
                node {
                    id
                    creationDate
                    vendor {
                        id
                        name
                    }
                }
            }
        }
    }
`;

export const APP_INSTALLATION_QUERY = gql`
    query AppInstallationQuery($id: ID!) {
        installation(id: $id) {
            id
            creationDate
            vendor {
                id
                name
            }
            oldRestApiCredentials {
                accessKey
                secretKey
            }
        }
    }
`;

/* =================================================
        VENDORS
   ================================================= */

export const VENDOR_QUERY = gql`
    query AppVendorSearchQuery($query: String, $after: String, $first: Int!) {
        appVendorSearch(query: $query, first: $first, after: $after) {
            totalCount
            edges {
                cursor
                node {
                    id
                    name
                }
            }
        }
    }
`;
